import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSidenav } from '@angular/material/sidenav';
import { DomSanitizer } from '@angular/platform-browser';

import { buildTimestamp } from '@shared-build-timestamp';

import { environment } from '../environments/environment';
import { IMatCustomSvgIcons } from './interfaces/custom-material-icons';
import { DatahubService } from './services/datahub.service';
import { UserService } from './services/user.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  standalone: false,
})
export class AppComponent implements OnInit {
  public readonly generalForemanAppUrl = `${environment.webServerUrl}gf`;
  public readonly buildTimestamp = buildTimestamp;
  public readonly isProduction = environment.production;
  public readonly isDevelopment = environment.development;
  public readonly environmentName = environment.environmentNameFull;
  public readonly environmentCssClass = `env-${environment.environmentName.toLowerCase()}`;

  //Remap some properties from injected services so we can keep those services private
  public get readySubject$() {
    return this.userSvc.readySubject$;
  }

  public get userAuthorizedSubject$() {
    return this.userSvc.authorizedSubject$;
  }

  public get unauthorizedErrorMessage() {
    return this.userSvc.unauthorizedErrorMessage;
  }

  public get userName(): string {
    return this.userSvc.getName();
  }

  public get landingPageUrl(): string {
    return environment.landingPageUrl;
  }

  public get buildDeploymentTitle(): string {
    return `This was deployed on ${this.buildTimestamp.full}`;
  }

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly dataHubSvc: DatahubService,
    private readonly userSvc: UserService,
    private readonly domSanitizer: DomSanitizer,
    private readonly matIconRegistry: MatIconRegistry,
  ) {
    this.registerCustomMatIcons();
  }

  public ngOnInit(): void {
    if (!this.isProduction) {
      //non-prod environments get the favicon swapped out to match the environment color
      const favicon = this.document.head.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (favicon) {
        favicon.href = favicon.href.replace('.png', `-${environment.environmentName.toLowerCase()}.png`);
      }
    }
  }

  public aboutClicked(drawer: MatSidenav): void {
    void drawer.close();
    this.dataHubSvc.launchResponsiveModal('about', null, { autoFocus: false, width: '630px' });
  }

  public help(drawer?: MatSidenav): void {
    if (drawer !== undefined) void drawer.close();
    this.dataHubSvc.launchResponsiveModal('need-help', null, { autoFocus: 'first-tabbable' });
  }

  public easyTicket() {
    const messageSeed = `I am unable to access the Davey Time application.\nEmployee Id: ${this.userSvc.employeeId} \n\nPlease investigate this issue and contact me when it is resolved.`;

    this.dataHubSvc.launchResponsiveModal(
      'need-help',
      {
        messageSeed,
        emailAddressOverride: 'daveytimesupport@davey.com',
        subjectOverride: `Davey Time Foreman App Access Issue`,
      },
      { autoFocus: 'first-tabbable' },
    );
  }

  public signOut(): void {
    this.userSvc.signOut();
  }

  private registerCustomMatIcons(): void {
    const customSvgIcons: IMatCustomSvgIcons[] = ['back-to-timesheet', 'help'];

    customSvgIcons.forEach((icon) => {
      this.matIconRegistry.addSvgIcon(
        icon,
        this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/custom-mat-icons/${icon}.svg`),
      );
    });
  }
}
